import { Icon } from '@iconify/react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { t } from '../../i18n';

/** `icon` is a raw Iconify name — the approved bar draws filled Solar glyphs
 *  (solar:home-2-bold and friends), chosen by each layout when it builds its
 *  tab list. */
export type Tab = { to: string; label: string; icon: string };

/**
 * Both shells draw the same navigation — the same NavLink, the same
 * `bottom-nav-item`, the same truncating label — once for the owner and once
 * for staff, top row and bottom bar. The only real difference is the round
 * button: the owner has one thing he does every morning, and staff do not.
 *
 * The glyphs are filled Solar icons now, not the 3D PNG set `BrandIcon` used
 * to wrap — the design's tab bar is filled shapes, orange where you are and
 * #C4C4C2 where you are not, 10px bold labels under 24px icons.
 */
export function DesktopTabs({
  tabs,
  className = 'app-container',
  trailing,
}: {
  tabs: Tab[];
  /** The staff shell narrows its column per route, so it passes its own. */
  className?: string;
  trailing?: ReactNode;
}) {
  return (
    <nav className="hidden border-b border-border bg-card sm:block" aria-label={t('Primary')}>
      <div className={`${className} flex gap-1 overflow-x-auto`}>
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              `flex items-center gap-2 px-3.5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`
            }
          >
            <Icon icon={tab.icon} className="h-5 w-5" aria-hidden />
            {tab.label}
          </NavLink>
        ))}
        {trailing}
      </div>
    </nav>
  );
}

export function BottomNav({
  tabs,
  center,
}: {
  tabs: Tab[];
  /** The round button. Given one, the bar splits 2 · button · rest around it. */
  center?: { label: string; onClick: () => void };
}) {
  const before = center ? tabs.slice(0, 2) : tabs;
  const after = center ? tabs.slice(2) : [];

  return (
    <nav className="bottom-nav" aria-label={t('Primary')}>
      {before.map((tab) => (
        <BottomTab key={tab.to} tab={tab} />
      ))}

      {center && (
        <div className="flex flex-1 items-center justify-center">
          <button
            type="button"
            onClick={center.onClick}
            aria-label={center.label}
            className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-primary text-white shadow-lg shadow-primary/30 transition-transform duration-150 active:scale-90"
          >
            <Icon icon="ph:plus-bold" className="text-2xl" aria-hidden />
          </button>
        </div>
      )}

      {after.map((tab) => (
        <BottomTab key={tab.to} tab={tab} />
      ))}
    </nav>
  );
}

function BottomTab({ tab }: { tab: Tab }) {
  return (
    <NavLink to={tab.to} end className="bottom-nav-item">
      <Icon icon={tab.icon} className="text-2xl" aria-hidden />
      <span className="truncate px-1">{tab.label}</span>
    </NavLink>
  );
}
